import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getMapGeolocationErrorMessage,
  MAP_GEOLOCATION_OPTIONS
} from '../utils/interactiveMapUtils';
import {
  hasBrowserGeolocation,
  hasNativeLocation,
  requestCurrentLocation
} from '../utils/nativeCapabilities';

const getInitialPermissionState = () => {
  if (!hasNativeLocation() && !hasBrowserGeolocation()) {
    return 'unsupported';
  }

  return 'prompt';
};

const useMapUserLocation = () => {
  const { t } = useTranslation();
  const [permissionState, setPermissionState] = useState(getInitialPermissionState);
  const [userLocation, setUserLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const [isLocating, setIsLocating] = useState(false);
  const hasAutoRequestedRef = useRef(false);

  const requestLocation = useCallback(() => {
    if (!hasNativeLocation() && !hasBrowserGeolocation()) {
      setPermissionState('unsupported');
      setLocationError(t('errors.geolocation.notSupported'));
      return;
    }

    setIsLocating(true);
    setLocationError(null);

    requestCurrentLocation(MAP_GEOLOCATION_OPTIONS)
      .then((position) => {
        setUserLocation({
          latitude: position.latitude,
          longitude: position.longitude,
          accuracy: position.accuracy
        });
        setPermissionState('granted');
        setLocationError(null);
        setIsLocating(false);
      })
      .catch((error) => {
        if (error?.code === 1) {
          setPermissionState('denied');
        }

        setLocationError(getMapGeolocationErrorMessage(error, t));
        setIsLocating(false);
      });
  }, [t]);

  useEffect(() => {
    if (!hasNativeLocation() && !hasBrowserGeolocation()) {
      setPermissionState('unsupported');
      return undefined;
    }

    if (hasNativeLocation()) {
      setPermissionState('prompt');
      return undefined;
    }

    if (!navigator.permissions?.query) {
      return undefined;
    }

    let isMounted = true;
    let detachPermissionListener = null;

    navigator.permissions
      .query({ name: 'geolocation' })
      .then((permissionStatus) => {
        if (!isMounted) {
          return;
        }

        setPermissionState(permissionStatus.state);

        const handlePermissionChange = () => {
          setPermissionState(permissionStatus.state);
        };

        if (typeof permissionStatus.addEventListener === 'function') {
          permissionStatus.addEventListener('change', handlePermissionChange);
          detachPermissionListener = () => {
            permissionStatus.removeEventListener('change', handlePermissionChange);
          };
        } else {
          permissionStatus.onchange = handlePermissionChange;
          detachPermissionListener = () => {
            permissionStatus.onchange = null;
          };
        }
      })
      .catch(() => {
        if (isMounted) {
          setPermissionState('prompt');
        }
      });

    return () => {
      isMounted = false;

      if (detachPermissionListener) {
        detachPermissionListener();
      }
    };
  }, []);

  useEffect(() => {
    if (permissionState !== 'granted' || userLocation || hasAutoRequestedRef.current) {
      return;
    }

    hasAutoRequestedRef.current = true;
    requestLocation();
  }, [permissionState, requestLocation, userLocation]);

  return {
    isLocating,
    locationError,
    permissionState,
    requestLocation,
    userLocation
  };
};

export default useMapUserLocation;
